import React from 'react';
import { Box, Typography } from '@mui/material';
import { AutoAwesome, ClearRounded, DataObject, LockOutlined } from '@mui/icons-material';
import { actionLabel, actionOf, bboxOf, outlineTone, shapeDisplayName, toPercentRect } from './deckModel';

/**
 * Outline looks. They sit on top of the client's slide image, not on Quill's surfaces, so they
 * are fixed colours with a light halo that keeps them visible on dark and light slides alike.
 * Colour is never the only cue: each tone has its own glyph in the corner badge (AI sparkle,
 * code braces, lock, cross), AI fields get a light tint and suggestions a dashed line. Badge
 * backgrounds are dark enough for white 10px text (at least 4.5:1).
 */
export const OUTLINE_TONES = Object.freeze({
  ai: { color: '#1B9E4B', badge: '#137A39', fill: 'rgba(27, 158, 75, 0.10)', style: 'solid', label: 'AI field', Icon: AutoAwesome },
  suggested: { color: '#1B9E4B', badge: '#137A39', fill: 'rgba(27, 158, 75, 0.05)', style: 'dashed', label: 'Suggested AI field', Icon: AutoAwesome },
  variable: { color: '#2F6FD0', badge: '#2459AB', fill: 'transparent', style: 'solid', label: 'Code variable', Icon: DataObject },
  fixed: { color: '#8A8F98', badge: '#5F646C', fill: 'transparent', style: 'solid', label: 'Fixed', Icon: LockOutlined },
  clear: { color: '#8A8F98', badge: '#5F646C', fill: 'transparent', style: 'dotted', label: 'Cleared on build', Icon: ClearRounded },
});

/**
 * Where a box's number badge goes: above the box when there is room (so it never covers the
 * slide text), else below it, else inside its top-left corner.
 * @param {{top: number, height: number}} rect - Box in % of the slide.
 * @returns {'above'|'below'|'inside'} Placement.
 */
export function badgePlacement(rect) {
  if (rect.top >= 8) return 'above';
  if (rect.top + rect.height <= 92) return 'below';
  return 'inside';
}

/** Badge position per placement (relative to the outline, outside its border). */
const BADGE_POSITION = {
  above: { bottom: '100%', left: -2, mb: '1px', borderRadius: '3px 3px 0 0' },
  below: { top: '100%', left: -2, mt: '1px', borderRadius: '0 0 3px 3px' },
  inside: { top: 0, left: 0, borderBottomRightRadius: '3px', opacity: 0.85 },
};

/**
 * One slide at the master's aspect ratio: the rendered PNG with an outline over every shape,
 * positioned from the shape's EMU geometry. Outlines are buttons (Tab reaches each one).
 * @param {object} props - Props.
 * @param {object} props.slide - Slide (from slidesOf).
 * @param {Array<object>} props.shapes - Shapes on the slide.
 * @param {Map<string, object>} props.fieldsByShape - Fields by shape key.
 * @param {{cx: number, cy: number}} props.slideSize - Slide size in EMU.
 * @param {string|null} [props.imageUrl] - Rendered slide image.
 * @param {string|null} [props.selectedShapeKey] - Selected shape.
 * @param {function(string|null): void} props.onSelectShape - Select a shape (null clears).
 * @param {boolean} [props.showOutlines] - Draw outlines ("as is" view hides them).
 * @param {string} [props.imageNote] - Shown when there is no image.
 * @param {function(): void} [props.onImageError] - The image failed to load (for example an expired URL).
 * @returns {React.ReactElement}
 */
function DeckCanvas({
  slide,
  shapes,
  fieldsByShape,
  slideSize,
  imageUrl = null,
  selectedShapeKey = null,
  onSelectShape,
  showOutlines = true,
  imageNote = '',
  onImageError,
}) {
  const ratio = `${slideSize.cx} / ${slideSize.cy}`;
  return (
    <Box
      data-testid="deck-canvas"
      data-slide-key={slide.key}
      data-aspect={ratio}
      onClick={(event) => {
        if (event.target === event.currentTarget || event.target.dataset?.canvasBackground === 'true') onSelectShape(null);
      }}
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: ratio,
        bgcolor: 'var(--q-subtle)',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '4px',
        overflow: 'hidden',
        boxShadow: 'var(--q-shadow-md)',
        opacity: slide.excluded ? 0.55 : 1,
      }}
    >
      {imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt={`Slide ${slide.number} as rendered from the master`}
          data-canvas-background="true"
          data-testid="deck-canvas-image"
          onError={onImageError}
          draggable={false}
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', objectFit: 'fill', userSelect: 'none' }}
        />
      ) : (
        <Box
          data-canvas-background="true"
          sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, textAlign: 'center' }}
        >
          <Typography data-canvas-background="true" variant="body2" color="text.secondary">
            {imageNote || 'The slide image is not available yet. The outlines show where each box sits.'}
          </Typography>
        </Box>
      )}
      {showOutlines && shapes.map((shape, index) => {
        const box = bboxOf(shape);
        if (!box) return null;
        const field = fieldsByShape.get(shape.key);
        const tone = outlineTone(shape, field);
        const look = OUTLINE_TONES[tone];
        const rect = toPercentRect(box, slideSize);
        const placement = badgePlacement(rect);
        const selected = shape.key === selectedShapeKey;
        const { Icon } = look;
        const name = shapeDisplayName(shape, index + 1);
        const status = tone === 'suggested' ? 'suggested AI field' : actionLabel(actionOf(shape, field)).toLowerCase();
        return (
          <Box
            key={shape.key}
            component="button"
            type="button"
            data-testid="deck-outline"
            data-shape-key={shape.key}
            data-tone={tone}
            data-badge={placement}
            aria-label={`Box ${index + 1}, ${name}: ${status}`}
            aria-pressed={selected}
            onClick={(event) => {
              event.stopPropagation();
              onSelectShape(shape.key);
            }}
            // Geometry inline (one value per shape), the look through sx. The EMU frame is the
            // unrotated one, so a rotated shape turns about its centre as PowerPoint draws it.
            style={{
              left: `${rect.left}%`,
              top: `${rect.top}%`,
              width: `${rect.width}%`,
              height: `${rect.height}%`,
              ...(box.rot ? { transform: `rotate(${box.rot}deg)`, transformOrigin: 'center' } : {}),
            }}
            sx={{
              all: 'unset',
              boxSizing: 'border-box',
              position: 'absolute',
              cursor: 'pointer',
              border: `${selected ? 3 : 2}px ${look.style} ${look.color}`,
              borderRadius: '2px',
              // Selection: the filled 4px ring. Keyboard focus: a separate dashed ring set off
              // from the box, so the focused box and the selected one never look the same.
              boxShadow: selected
                ? '0 0 0 1px rgba(255,255,255,0.9), 0 0 0 4px var(--q-focus-ring)'
                : '0 0 0 1px rgba(255,255,255,0.75)',
              bgcolor: selected ? 'rgba(79, 59, 214, 0.08)' : look.fill,
              zIndex: selected ? 3 : 2,
              transition: 'background-color 120ms ease',
              '&:hover': { bgcolor: 'rgba(79, 59, 214, 0.06)' },
              '&:hover > span, &:focus-visible > span': { opacity: 1 },
              '&:focus-visible': { outline: '2px dashed var(--q-focus-ring)', outlineOffset: '4px', zIndex: 4 },
            }}
          >
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                position: 'absolute',
                ...BADGE_POSITION[placement],
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px',
                minWidth: 16,
                height: 16,
                px: '3px',
                fontSize: 10,
                lineHeight: '16px',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                color: '#fff',
                bgcolor: look.badge,
                fontFamily: 'var(--q-font-sans)',
                pointerEvents: 'none',
              }}
            >
              <Icon data-testid={`deck-badge-icon-${tone}`} sx={{ fontSize: 10 }} />
              {index + 1}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default DeckCanvas;
